import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { AccountService } from 'app/account/account.service';
import { IdentityService } from 'app/core/identity/identity.service';
import { SiteRoleService } from 'app/core/identity/site-role.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { Environment, ENVIRONMENT_DI } from 'app/environment/environment';
import { Note } from 'app/shared/note-info/note-info';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { TRIGGERBEE_LINK_HELP } from 'app/shared/utils/triggerbee-link-help';
import { ClipboardService } from 'ngx-clipboard';
import { firstValueFrom, Subscription } from 'rxjs';
import { IntegrationType } from '../integrations-models';
import { ThemeService } from 'app/shared/services/theme.service';

@Component({
  selector: 'app-zapier-embed',
  templateUrl: './zapier-embed.component.html',
  styleUrls: ['./zapier-embed.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ZapierEmbedComponent implements OnInit, OnDestroy {
  @Input() public introCopyDisplay: string = 'show';
  @Input() public manageZapsDisplay: string = 'show';
  @Input() public appSelectionDisplay: string = 'show';
  @Input() public appSearchBarDisplay: string = 'show';
  @Input() public zapCreateFromScratchDisplay: string = 'show';
  @Input() public guessZapDisplay: string = 'show';
  @Input() public templateLimit: number = 10;
  @Input() public templateCtaDisplay: string = 'show';

  public userTheme: string = 'auto';
  public clientId: string;
  public apiKey: string | undefined;
  public isLoading: boolean = true;
  public note: Note = {
    message: 'settings.apps.Zapier.workflowsApiKeyText',
    link: TRIGGERBEE_LINK_HELP.zapier,
    textLink: 'settings.apps.Zapier.helpLinkText',
    type: 'generic.note',
  };
  public isDarkThemeSub: Subscription;
  private subscriptionArray: Subscription[] = [];
  private readonly identityService = inject(IdentityService);
  private readonly siteRoleService = inject(SiteRoleService);

  constructor(
    private readonly snackbar: SnackBarService,
    private integrationsService: IntegrationsService,
    private readonly clipboardService: ClipboardService,
    private readonly accountService: AccountService,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly themeService: ThemeService,
    @Inject(ENVIRONMENT_DI) environment: Environment,
  ) {
    this.clientId = environment.zapierEmbedClientId;
    this.loadExternalResources();
  }

  ngOnInit() {
    this.isDarkThemeSub = this.themeService.isDarkThemeBS.subscribe((isDarkTheme) => {
      this.userTheme = isDarkTheme ? 'dark' : 'light';
    });
    this.subscriptionArray.push(this.isDarkThemeSub);
    this.initializeComponent();
  }

  private async initializeComponent() {
    try {
      await Promise.all([this.initializeZapierIntegration(), this.loadApiKey()]);
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private loadExternalResources(): void {
    this.loadStyle('https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css');
    this.loadScript('https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js');
  }

  private async loadApiKey(): Promise<void> {
    const canSeeApiKeys =
      !!this.identityService.currentIdentity?.isAdmin() || (await this.siteRoleService.isAccountAdminAsync());
    if (!canSeeApiKeys) {
      this.note = { ...this.note, message: 'settings.apps.Zapier.workflowsApiKeyAccountAdminOnly' };
      return;
    }
    const apiKeys = await firstValueFrom(this.accountService.getApiKeys());
    if (apiKeys) {
      const index = apiKeys.findIndex((k) => '' + k.type === '' + IntegrationType.Zapier);
      if (index !== -1) {
        this.apiKey = apiKeys[index].id;
      }
    }
  }

  private async initializeZapierIntegration(): Promise<void> {
    const settings = await firstValueFrom(this.integrationsService.getIntegrationDetails(IntegrationType.Zapier));
    if (!settings) {
      await firstValueFrom(this.integrationsService.authenticateIntegration(IntegrationType.Zapier, {}));
    }
  }

  public copyToClipboard(content: string) {
    this.clipboardService.copyFromContent(content);
    this.snackbar.success('generic.copyToClipboard.copied');
  }

  private loadScript(scriptUrl: string): void {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = scriptUrl;
    document.head.appendChild(script);
  }

  private loadStyle(styleUrl: string): void {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = styleUrl;
    document.head.appendChild(link);
  }

  ngOnDestroy(): void {
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
